import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [pastFirst, setPastFirst] = useState(false);
  const location = useLocation();
  const isMediaRoute = location.pathname.startsWith('/media');

  useEffect(() => {
    // Cache window.innerHeight to avoid forced reflows during scroll
    let cachedWindowHeight = window.innerHeight;
    
    const handleResize = () => {
      cachedWindowHeight = window.innerHeight;
    };
    
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      setPastFirst(isMediaRoute ? y >= cachedWindowHeight : false);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMediaRoute]);

  const baseTextColor = isMediaRoute && !pastFirst ? 'rgba(255, 255, 255, 1)' : 'rgba(57, 55, 55, 1)';
  const scrolledBg = isMediaRoute && !pastFirst ? 'rgba(9, 37, 84, 0.85)' : 'rgba(242, 243, 238, 0.85)';
  const handleNavClick = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      // Use offsetTop to avoid forced reflow, cache the navbar height
      const offsetPosition = element.offsetTop - 80; // Account for fixed navbar
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const navLinks = [{
    targetId: "participation-criteria",
    label: "Register"
  }, {
    targetId: "timing",
    label: "Timeline"
  }, {
    targetId: "tracks",
    label: "Tracks"
  }, {
    targetId: "mentors",
    label: "Mentors"
  }, {
    targetId: "faq",
    label: "FAQ"
  }];
  return <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-4" style={{
    backgroundColor: isScrolled || isOpen ? scrolledBg : 'transparent',
    backdropFilter: isScrolled || isOpen ? 'blur(12px)' : 'none',
    borderBottom: (isScrolled || isOpen) ? ((isMediaRoute && !pastFirst) ? 'none' : '1px solid rgba(239, 239, 239, 1)') : 'none'
  }}>
      <div className="container mx-auto px-0 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between ml-[15px] mr-0 lg:ml-0">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold" style={{
            color: baseTextColor
          }} aria-label="ai4hack home">
              ai<span style={{
              color: 'rgba(242, 76, 39, 1)'
            }}>4</span>hack
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link
                key={link.targetId}
                to={{ pathname: "/", hash: `#${link.targetId}` }}
                onClick={(e) => {
                  if (location.pathname === "/") {
                    e.preventDefault();
                    handleNavClick(link.targetId);
                  }
                }}
                className="relative inline-block py-2 transition-colors duration-300 group after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-[2px] after:bottom-0 after:left-0 after:bg-[rgba(242,76,39,1)] after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                style={{ color: baseTextColor }}
              >
                <span className="transition-colors duration-300 group-hover:text-[rgba(242,76,39,1)]">{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-md transition-colors duration-200" style={{
            color: baseTextColor
          }}>
              <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                {isOpen ? <path fillRule="evenodd" d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z" /> : <path fillRule="evenodd" d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z" />}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && <div className="md:hidden mt-4 pb-4">
            <div className="flex flex-col space-y-3">
              {navLinks.map(link => (
                <Link
                  key={link.targetId}
                  to={{ pathname: "/", hash: `#${link.targetId}` }}
                  onClick={(e) => {
                    setIsOpen(false);
                    if (location.pathname === "/") {
                      e.preventDefault();
                      handleNavClick(link.targetId);
                    }
                  }}
                  className="relative inline-block py-2 px-3 rounded-md text-left transition-colors duration-200 group after:content-[''] after:absolute after:w-full after:scale-x-0 after:h-[2px] after:bottom-0 after:left-0 after:bg-[rgba(242,76,39,1)] after:origin-bottom-right after:transition-transform after:duration-300 hover:after:scale-x-100 hover:after:origin-bottom-left"
                  style={{ color: baseTextColor }}
                >
                  <span className="transition-colors duration-200 group-hover:text-[rgba(242,76,39,1)]">{link.label}</span>
                </Link>
              ))}
            </div>
          </div>}
      </div>
    </nav>;
};
export default Navbar;